/* eslint-disable padding-line-between-statements */
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import moment from 'moment';
import { GatewayEndorsementService as LoadSaveService} from 'twia-capability-gateway-policychange';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import TWIADateFormatUtil from "ent-capability-quoteandbind-common-react/util/TWIADateFormatUtil";
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import { useModal } from '@jutro/components';
import { TWIAAccountHolderEdit } from 'twia-capability-quoteandbind-residential-react'
import { DateUtil, LocalDateUtil } from '@xengage/gw-portals-util-js';
import TWIAWizardHelper from 'producer-engage/src/overrides/modules-react/twia-portals-wizard-components-ui/Wizard/components/util/TWIAWizardHelper.js';
import { useTranslator } from '@jutro/locale';
import metadata from './PolicyChangeDetails.metadata.json5';
import messages from './PolicyChangeDetails.messages.js';
import styles from './PolicyChangeDetails.module.scss'

function TWIAChangePolicyDetailsPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext)
    const { wizardData: submissionVM, updateWizardData } = props;
    const { isComponentValid, onValidate, initialValidation} = useValidation('TWIAChangePolicyDetailsPage');
    const { authHeader } = useAuthentication();
    const { showAlert } = useModal();
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const [editPrimaryName, setEditPrimaryName] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [showMinDateMessage, setShowMinDateMessage] = useState(false);
    const minimumEffectiveDate = _.get(submissionVM, `baseData.minimumEffectiveDate.value`);
    const formattedMinEffectiveDate = TWIADateFormatUtil.formatDate(minimumEffectiveDate);
    const intEffectiveDate = _.get(submissionVM, `baseData.initialEffectiveDate.value`);
    const today = LocalDateUtil.today();
    const isMinDateValid = moment(intEffectiveDate).isSameOrAfter(today);
    const periodEndDate = _.get(submissionVM, `baseData.periodEndDate.value`);
    const periodStartDate = _.get(submissionVM, `baseData.periodStartDate.value`);
    const referenceID = _.get(submissionVM, 'jobID.value') || _.get(submissionVM,'policyChange.jobID.value');
    const [renewalInfo, setRenewalInfo] = useState();
    const [isPolicyChangeForFutureTerm, setIsPolicyChangeForFutureTerm] = useState();
 
    const formatPolicyPeriod = (startDate, endDate) => {
        const formattedStartDate = TWIADateFormatUtil.formatDate(startDate);
        const formattedEndDate = TWIADateFormatUtil.formatDate(endDate);

        return `${formattedStartDate} - ${formattedEndDate}`;
    };

    // DE18438
    useEffect(() => {
        if (referenceID !== undefined) {
            LoadSaveService.getRenewalInfo_TWIA(referenceID, authHeader).then((response) => {

                if (response !== null) {
                    setRenewalInfo(response)

                    const now = DateUtil.currentDate().getTime();
                    const psDate = _.get(submissionVM, 'baseData.periodStartDate') || _.get(submissionVM, 'policyChange.baseData.periodStartDate');
                    const futureTermDate = new Date(psDate.year, psDate.month, psDate.day).getTime() > now

                    setIsPolicyChangeForFutureTerm(futureTermDate);
                }

            })
        }
    }, [])

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            // DE17433
            const todayDate = LocalDateUtil.today();
            const isMinimumDateValid = moment(value).isSameOrAfter(todayDate);
            if (path === 'baseData.initialEffectiveDate') {
                _.set(submissionVM, 'baseData.effectiveDate', value);
                setShowMinDateMessage(!isMinimumDateValid);
            }
            updateWizardData(submissionVM);
            refreshData();
        },
        [refreshData, submissionVM, updateWizardData]
    );

    const onNext = useCallback(async () => {
        if (editPrimaryName) {
            return showAlert({
                title: messages.saveModalTitle,
                message: messages.saveModalText,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            }).then(() => false)
        }

        if (!isComponentValid) {
            setShowErrors(true)

            return false
        }

        try {
            _.unset(submissionVM.value, 'bindData');
            _.set(submissionVM, 'baseData.policyAddress.mustStandardize_Ext', false)
            _.set(submissionVM, 'lobData.commercialProperty.coverages', undefined) // DE17700
            _.set(submissionVM, 'lobData.commercialProperty.coverables', undefined)

            const newSubmissionVM = viewModelService.clone(submissionVM);
            // _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.saveEndorsement(
                newSubmissionVM.value,
                authHeader
            )

            return submissionVM
        } catch (error) {
            console.log(error)
            const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');
            const isTWIA = selectedUWCompany === 'twia'
            ErrorPopupUtil.showSaveErrorPopup(showAlert, isTWIA);
            
            return false;
        }
    }, [editPrimaryName, isComponentValid, showAlert, submissionVM, viewModelService, authHeader]);

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        accountHolderEdit: {
            fieldLabel: messages.fieldLabel,
            submissionVM,
            updateWizardData,
            editPrimaryName,
            setEditPrimaryName
        },
        minDateMsg: {
            value: `Minimum date is ${formattedMinEffectiveDate}`, // DE17433
            visible: showMinDateMessage,
        },
        proposedPolicyPeriod: {
            value: formatPolicyPeriod(periodStartDate, periodEndDate)
        },
        renewalSchMessage:{
            content : translator(messages.renewalScheduledMessage),
            visible : renewalInfo?.renewalStatus_TWIA === 'Bound' && !isPolicyChangeForFutureTerm
                && !_.isUndefined(renewalInfo)
        },
    };

    const resolvers = {
        resolveComponentMap: {
            TWIAAccountHolderEdit
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
           // onValidate: onValidate,
        },
    };

    const skip = useCallback(async () => {
        if (!isMinDateValid) {
            return false; // DE17904
        }

        return TWIAWizardHelper.skipStep(submissionVM, initialValidation);
    }, [isMinDateValid, initialValidation, submissionVM]); 

    return (
        <WizardPage
            onNext={onNext}
            skipWhen={skip}
            disableNext={!isMinDateValid || !submissionVM.baseData.initialEffectiveDate.aspects.valid}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                showErrors={showErrors}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

TWIAChangePolicyDetailsPage.propTypes = wizardProps;
export default TWIAChangePolicyDetailsPage;